/**
 * Web component: Sticky Add to Cart (sticky-add-to-cart)
 * @customElement sticky-add-to-cart
 * ----------------------------------------------------------------------------
 * Shows a sticky bar at the bottom when the main add-to-cart button scrolls out of view.
 * Uses IntersectionObserver to track the main button's visibility.
 * Includes accessibility support for keyboard navigation and screen readers.
 * @example
 * <sticky-add-to-cart></sticky-add-to-cart>
 */
if (!customElements.get("sticky-add-to-cart")) {
  customElements.define(
    "sticky-add-to-cart",
    class StickyAddToCart extends HTMLElement {
      /**
       * Custom element constructor.
       * - Always call super first in constructor.
       */
      constructor() {
        super();
        this.mainButton = document.querySelector("[js-main-product-submit]");
        this.addToCartButton = null;
        this.observer = null;
        this.isVisible = false;
      }

      /**
       * Lifecycle hook - component is attached to the DOM.
       * Sets up observer, checks initial visibility, and adds accessibility attributes.
       */
      connectedCallback() {
        if (!this.mainButton) return;

        this.initAccessibility();
        this.addToCartButton = this.querySelector("[type='submit']");
        this.setupIntersectionObserver();
        this.updateVisibility(false);
      }

      /**
       * Lifecycle hook - component is removed from the DOM.
       * Clean up the observer to prevent memory leaks.
       */
      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }
      }

      /**
       * Set up initial accessibility attributes for screen readers and keyboard navigation.
       * Configures the component with proper ARIA roles and states.
       */
      initAccessibility() {
        this.setAttribute("role", "region");
        this.setAttribute("aria-label", "Sticky add to cart");
        this.setAttribute("aria-hidden", "true");
        this.setAttribute("tabindex", "-1");
      }

      /**
       * Initialize IntersectionObserver to watch the main button.
       * Uses a small negative margin to trigger slightly before button is hidden.
       */
      setupIntersectionObserver() {
        const options = {
          threshold: 0,
          rootMargin: "-10px 0px 0px 0px",
        };

        this.observer = new IntersectionObserver((entries) => {
          const entry = entries[0];
          const mainButtonVisible = entry ? entry.isIntersecting : false;
          this.updateVisibility(!mainButtonVisible);
        }, options);

        this.observer.observe(this.mainButton);
      }

      /**
       * Update the visibility state and accessibility attributes.
       * Manages component appearance, ARIA states, and focus handling.
       * 
       * @param {boolean} shouldBeVisible - Whether the sticky bar should be visible
       */
      updateVisibility(shouldBeVisible) {
        // Only update if state is changing
        if (shouldBeVisible === this.isVisible) return;

        this.isVisible = shouldBeVisible;
        this.classList.toggle("visible", shouldBeVisible);
        document.body.classList.toggle("sticky-add-to-cart-visible", shouldBeVisible);
        this.setAttribute("aria-hidden", shouldBeVisible ? "false" : "true");
        this.setAttribute("tabindex", shouldBeVisible ? "0" : "-1");

        // Handle focus management when becoming visible
        if (shouldBeVisible && this.addToCartButton && this.isKeyboardUser()) {
          // Small delay to ensure the component is rendered
          setTimeout(() => this.addToCartButton.focus(), 100);
        }
      }

      /**
       * Determines if the user is likely using keyboard navigation
       * by checking if the last interaction was a keyboard event.
       * 
       * @returns {boolean} True if the user is likely using keyboard navigation
       */
      isKeyboardUser() {
        return (
          document.activeElement &&
          document.activeElement.tagName !== "BODY" &&
          document.activeElement !== this.mainButton
        );
      }
    }
  );
}
